function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to read image."));
    image.src = src;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to encode image."));
    reader.readAsDataURL(blob);
  });
}

export async function optimizeImageFile(file, maxSide = 720) {
  if (!file || !String(file.type || "").startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) throw new Error("Unable to process image.");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    // Keep the upload comfortably below common mini-app/server body limits.
    // Quality is reduced only when the encoded result is still unusually large.
    const maxOutputBytes = 700 * 1024;
    let blob = null;
    let mimeType = "image/webp";
    for (const quality of [0.82, 0.72, 0.62, 0.52]) {
      blob = await canvasToBlob(canvas, mimeType, quality);
      if (!blob) {
        mimeType = "image/jpeg";
        blob = await canvasToBlob(canvas, mimeType, quality);
      }
      if (blob && blob.size <= maxOutputBytes) break;
    }
    if (!blob) throw new Error("Unable to encode image.");
    const dataUrl = await blobToDataUrl(blob);

    return {
      dataUrl,
      blob,
      mimeType: blob.type || mimeType,
      width,
      height,
      aspectRatio: width / height
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function luminance(data, index) {
  return data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
}

function pixelIndex(x, y, width) {
  return (y * width + x) * 4;
}

function buildCandidates(imageData, width, height) {
  const { data } = imageData;
  let transparentPixels = 0;
  let graySum = 0;
  let grayCount = 0;

  for (let y = 0; y < height; y += 3) {
    for (let x = 0; x < width; x += 3) {
      const index = pixelIndex(x, y, width);
      if (data[index + 3] < 220) transparentPixels += 1;
      graySum += luminance(data, index);
      grayCount += 1;
    }
  }

  const hasTransparency = transparentPixels > grayCount * 0.03;
  const meanGray = grayCount ? graySum / grayCount : 128;
  const candidates = [];

  for (let y = 2; y < height - 2; y += 2) {
    for (let x = 2; x < width - 2; x += 2) {
      const index = pixelIndex(x, y, width);
      const alpha = data[index + 3] / 255;
      if (hasTransparency && alpha < 0.08) continue;

      const left = pixelIndex(x - 1, y, width);
      const right = pixelIndex(x + 1, y, width);
      const up = pixelIndex(x, y - 1, width);
      const down = pixelIndex(x, y + 1, width);

      const gx = Math.abs(luminance(data, right) - luminance(data, left));
      const gy = Math.abs(luminance(data, down) - luminance(data, up));
      const alphaGx = Math.abs(data[right + 3] - data[left + 3]);
      const alphaGy = Math.abs(data[down + 3] - data[up + 3]);
      const gray = luminance(data, index);
      const maxChannel = Math.max(data[index], data[index + 1], data[index + 2]);
      const minChannel = Math.min(data[index], data[index + 1], data[index + 2]);
      const saturation = maxChannel - minChannel;

      let score = Math.hypot(gx, gy) + saturation * 0.16 + Math.abs(gray - meanGray) * 0.12;
      if (hasTransparency) score += Math.hypot(alphaGx, alphaGy) * 1.25 + alpha * 12;

      candidates.push({ x, y, score });
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates.slice(0, 3200);
}

function selectDistributedPoints(candidates, count, width, height) {
  if (!candidates.length) {
    return Array.from({ length: count }, (_, index) => {
      const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
      return {
        x: width * (0.5 + Math.cos(angle) * 0.35),
        y: height * (0.5 + Math.sin(angle) * 0.35)
      };
    });
  }

  const selected = [candidates[0]];
  const maxScore = Math.max(1, candidates[0].score);
  const diagonal = Math.hypot(width, height) || 1;

  while (selected.length < count) {
    let best = null;
    let bestValue = -Infinity;

    for (const candidate of candidates) {
      if (selected.includes(candidate)) continue;
      let minDistance = Infinity;
      for (const chosen of selected) {
        const distance = Math.hypot(candidate.x - chosen.x, candidate.y - chosen.y);
        if (distance < minDistance) minDistance = distance;
      }

      const edgeWeight = Math.min(1, candidate.score / maxScore);
      const spreadWeight = Math.min(1, minDistance / (diagonal * 0.32));
      const value = edgeWeight * 0.42 + spreadWeight * 0.58;
      if (value > bestValue) {
        bestValue = value;
        best = candidate;
      }
    }

    if (!best) break;
    selected.push(best);
  }

  return selected;
}

function orderAsRoute(points) {
  if (points.length <= 2) return points;
  const remaining = [...points];
  let startIndex = 0;
  for (let index = 1; index < remaining.length; index += 1) {
    const current = remaining[index];
    const best = remaining[startIndex];
    if (current.y < best.y || (current.y === best.y && current.x < best.x)) startIndex = index;
  }

  const route = [remaining.splice(startIndex, 1)[0]];
  while (remaining.length) {
    const previous = route[route.length - 1];
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    for (let index = 0; index < remaining.length; index += 1) {
      const candidate = remaining[index];
      const distance = Math.hypot(candidate.x - previous.x, candidate.y - previous.y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }
    route.push(remaining.splice(nearestIndex, 1)[0]);
  }
  return route;
}

export async function generateStarNodes(imageDataUrl, count = 25) {
  const image = await loadImage(imageDataUrl);
  const maxAnalysisSide = 190;
  const scale = Math.min(1, maxAnalysisSide / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(64, Math.round(image.naturalWidth * scale));
  const height = Math.max(64, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const imageData = context.getImageData(0, 0, width, height);
  const candidates = buildCandidates(imageData, width, height);
  const distributed = selectDistributedPoints(candidates, count, width, height);
  const route = orderAsRoute(distributed);

  return route.slice(0, count).map((point, index) => {
    const x = Math.max(0.035, Math.min(0.965, point.x / width));
    const y = Math.max(0.035, Math.min(0.965, point.y / height));
    const sampleX = Math.max(0, Math.min(width - 1, Math.round(point.x)));
    const sampleY = Math.max(0, Math.min(height - 1, Math.round(point.y)));
    const sampleIndex = pixelIndex(sampleX, sampleY, width);
    const r = imageData.data[sampleIndex] ?? 127;
    const g = imageData.data[sampleIndex + 1] ?? 248;
    const b = imageData.data[sampleIndex + 2] ?? 239;
    const color = `#${[r, g, b].map(value => value.toString(16).padStart(2, "0")).join("")}`;
    return {
      id: `node-${index + 1}`,
      level: index + 1,
      x,
      y,
      color
    };
  });
}
